import React, { useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    Typography,
    TableContainer,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Paper,
    IconButton,
    Chip,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

const PANEL_TYPES = {
    TEXT: 'text',
    TABLE: 'table',
    FILES: 'files',
    USERS: 'users',
    CUSTOM: 'custom',
    JUDGING_DASHBOARD: 'judging_dashboard'
};

const TABLE_COLUMN_TYPES = {
    TEXT: 'text',
    NUMBER: 'number',
    DATE: 'date',
    SELECT: 'select',
    PERSON: 'person'
};

const PanelModal = ({ open, onClose, panel, onSave, isEditing = false }) => {
    const [newPanel, setNewPanel] = React.useState({
        title: '',
        type: PANEL_TYPES.TEXT,
        description: '',
        files: [],
        tableConfig: {
            columns: [],
            rows: []
        },
        users: [],
        sections: []
    });

    useEffect(() => {
        if (isEditing && panel) {
            setNewPanel(panel);
        } else {
            setNewPanel({
                title: '',
                type: PANEL_TYPES.TEXT,
                description: '',
                files: [],
                tableConfig: {
                    columns: [],
                    rows: []
                },
                users: [],
                sections: []
            });
        }
    }, [isEditing, panel]);

    const handlePanelFieldChange = (field, value) => {
        setNewPanel((prev) => ({ ...prev, [field]: value }));
    };

    const handlePanelTypeChange = (type) => {
        setNewPanel(prev => ({
            ...prev,
            type,
            files: type === PANEL_TYPES.FILES ? prev.files : [],
            tableConfig: type === PANEL_TYPES.TABLE ? prev.tableConfig : { columns: [], rows: [] },
            users: type === PANEL_TYPES.USERS ? prev.users : [],
            sections: type === PANEL_TYPES.CUSTOM ? prev.sections : []
        }));
    };

    const handleFileUpload = (event) => {
        const files = Array.from(event.target.files);
        setNewPanel(prev => ({
            ...prev,
            files: [...prev.files, ...files]
        }));
    };

    const handleRemoveFile = (index) => {
        setNewPanel(prev => ({
            ...prev,
            files: prev.files.filter((_, i) => i !== index)
        }));
    };

    const handleAddTableColumn = () => {
        setNewPanel(prev => ({
            ...prev,
            tableConfig: {
                ...prev.tableConfig,
                columns: [...prev.tableConfig.columns, {
                    id: `col-${Date.now()}`,
                    name: '',
                    type: TABLE_COLUMN_TYPES.TEXT
                }]
            }
        }));
    };

    const handleRemoveTableColumn = (columnId) => {
        setNewPanel(prev => ({
            ...prev,
            tableConfig: {
                ...prev.tableConfig,
                columns: prev.tableConfig.columns.filter(col => col.id !== columnId)
            }
        }));
    };

    const handleAddSection = () => {
        setNewPanel(prev => ({
            ...prev,
            sections: [...prev.sections, {
                id: `section-${Date.now()}`,
                title: '',
                description: ''
            }]
        }));
    };

    const handleSave = () => {
        onSave(newPanel);
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle>
                {isEditing ? 'Edit Panel' : 'Create New Panel'}
                <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
                    Customize your panel with different content types and layouts
                </Typography>
            </DialogTitle>
            <DialogContent>
                <Box sx={{ mt: 2 }}>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Panel Title"
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={newPanel.title}
                        onChange={(e) => handlePanelFieldChange('title', e.target.value)}
                        required
                    />
                    
                    <FormControl fullWidth margin="dense">
                        <InputLabel>Panel Type</InputLabel>
                        <Select
                            value={newPanel.type}
                            onChange={(e) => handlePanelTypeChange(e.target.value)}
                            label="Panel Type"
                        >
                            <MenuItem value={PANEL_TYPES.TEXT}>Text Content</MenuItem>
                            <MenuItem value={PANEL_TYPES.TABLE}>Table</MenuItem>
                            <MenuItem value={PANEL_TYPES.FILES}>File Upload</MenuItem>
                            <MenuItem value={PANEL_TYPES.USERS}>User Tags</MenuItem>
                            <MenuItem value={PANEL_TYPES.CUSTOM}>Custom Sections</MenuItem>
                            <MenuItem value={PANEL_TYPES.JUDGING_DASHBOARD}>Hackathon Judging Dashboard</MenuItem>
                        </Select>
                    </FormControl>

                    {newPanel.type === PANEL_TYPES.TEXT && (
                        <TextField
                            margin="dense"
                            label="Panel Description"
                            type="text"
                            fullWidth
                            variant="outlined"
                            value={newPanel.description}
                            onChange={(e) => handlePanelFieldChange('description', e.target.value)}
                            multiline
                            minRows={3}
                            sx={{ mt: 2 }}
                        />
                    )}

                    {newPanel.type === PANEL_TYPES.TABLE && (
                        <Box sx={{ mt: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="subtitle1">Table Configuration</Typography>
                                <Button
                                    startIcon={<AddCircleOutlineIcon />}
                                    onClick={handleAddTableColumn}
                                    variant="outlined"
                                    size="small"
                                >
                                    Add Column
                                </Button>
                            </Box>
                            <TableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            {newPanel.tableConfig.columns.map((column) => (
                                                <TableCell key={column.id}>
                                                    <TextField
                                                        size="small"
                                                        value={column.name}
                                                        onChange={(e) => {
                                                            const newColumns = newPanel.tableConfig.columns.map(col =>
                                                                col.id === column.id ? { ...col, name: e.target.value } : col
                                                            )
                                                            setNewPanel(prev => ({
                                                                ...prev,
                                                                tableConfig: { ...prev.tableConfig, columns: newColumns }
                                                            }))
                                                        }}
                                                        placeholder="Column Name"
                                                    />
                                                    <Select
                                                        size="small"
                                                        value={column.type}
                                                        onChange={(e) => {
                                                            const newColumns = newPanel.tableConfig.columns.map(col =>
                                                                col.id === column.id ? { ...col, type: e.target.value } : col
                                                            )
                                                            setNewPanel(prev => ({
                                                                ...prev,
                                                                tableConfig: { ...prev.tableConfig, columns: newColumns }
                                                            }))
                                                        }}
                                                        sx={{ mt: 1, width: '100%' }}
                                                    >
                                                        {Object.values(TABLE_COLUMN_TYPES).map(type => (
                                                            <MenuItem key={type} value={type}>
                                                                {type.charAt(0).toUpperCase() + type.slice(1)}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleRemoveTableColumn(column.id)}
                                                        sx={{ mt: 1 }}
                                                    >
                                                        <DeleteOutlineIcon />
                                                    </IconButton>
                                                </TableCell>
                                            ))}
                                            <TableCell width={100}>Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {newPanel.tableConfig.rows.map((row, rowIndex) => (
                                            <TableRow key={rowIndex}>
                                                {newPanel.tableConfig.columns.map((column) => (
                                                    <TableCell key={column.id}>
                                                        <TextField
                                                            size="small"
                                                            value={row[column.id] || ''}
                                                            onChange={(e) => {
                                                                const newRows = newPanel.tableConfig.rows.map((r, i) =>
                                                                    i === rowIndex ? { ...r, [column.id]: e.target.value } : r
                                                                )
                                                                setNewPanel(prev => ({
                                                                    ...prev,
                                                                    tableConfig: { ...prev.tableConfig, rows: newRows }
                                                                }))
                                                            }}
                                                        />
                                                    </TableCell>
                                                ))}
                                                <TableCell>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => {
                                                            setNewPanel(prev => ({
                                                                ...prev,
                                                                tableConfig: {
                                                                    ...prev.tableConfig,
                                                                    rows: prev.tableConfig.rows.filter((_, i) => i !== rowIndex)
                                                                }
                                                            }))
                                                        }}
                                                    >
                                                        <DeleteOutlineIcon />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <Button
                                variant="outlined"
                                startIcon={<AddCircleOutlineIcon />}
                                sx={{ mt: 2 }}
                                onClick={() => {
                                    const emptyRow = {};
                                    newPanel.tableConfig.columns.forEach(col => {
                                        emptyRow[col.id] = '';
                                    });
                                    setNewPanel(prev => ({
                                        ...prev,
                                        tableConfig: {
                                            ...prev.tableConfig,
                                            rows: [...prev.tableConfig.rows, emptyRow]
                                        }
                                    }));
                                }}
                                disabled={newPanel.tableConfig.columns.length === 0}
                            >
                                Add Row
                            </Button>
                        </Box>
                    )}

                    {newPanel.type === PANEL_TYPES.FILES && (
                        <Box sx={{ mt: 2 }}>
                            <input
                                accept="*/*"
                                style={{ display: 'none' }}
                                id="file-upload"
                                type="file"
                                multiple
                                onChange={handleFileUpload}
                            />
                            <label htmlFor="file-upload">
                                <Button
                                    variant="outlined"
                                    component="span"
                                    startIcon={<CloudUploadIcon />}
                                >
                                    Upload Files
                                </Button>
                            </label>
                            <Box sx={{ mt: 2 }}>
                                {newPanel.files.map((file, index) => (
                                    <Chip
                                        key={index}
                                        label={file.name}
                                        onDelete={() => handleRemoveFile(index)}
                                        sx={{ m: 0.5 }}
                                    />
                                ))}
                            </Box>
                        </Box>
                    )}

                    {newPanel.type === PANEL_TYPES.CUSTOM && (
                        <Box sx={{ mt: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="subtitle1">Custom Sections</Typography>
                                <Button
                                    startIcon={<AddCircleOutlineIcon />}
                                    onClick={handleAddSection}
                                    variant="outlined"
                                    size="small"
                                >
                                    Add Section
                                </Button>
                            </Box>
                            {newPanel.sections.map((section, index) => (
                                <Box key={section.id} sx={{ mb: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                        <Typography variant="subtitle2">Section {index + 1}</Typography>
                                        <IconButton
                                            size="small"
                                            onClick={() => {
                                                setNewPanel(prev => ({
                                                    ...prev,
                                                    sections: prev.sections.filter(s => s.id !== section.id)
                                                }))
                                            }}
                                        >
                                            <DeleteOutlineIcon />
                                        </IconButton>
                                    </Box>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Section Title"
                                        value={section.title}
                                        onChange={(e) => {
                                            const newSections = newPanel.sections.map(s =>
                                                s.id === section.id ? { ...s, title: e.target.value } : s
                                            )
                                            setNewPanel(prev => ({
                                                ...prev,
                                                sections: newSections
                                            }))
                                        }}
                                        sx={{ mb: 1 }}
                                    />
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Section Description"
                                        multiline
                                        rows={2}
                                        value={section.description}
                                        onChange={(e) => {
                                            const newSections = newPanel.sections.map(s =>
                                                s.id === section.id ? { ...s, description: e.target.value } : s
                                            )
                                            setNewPanel(prev => ({
                                                ...prev,
                                                sections: newSections
                                            }))
                                        }}
                                    />
                                </Box>
                            ))}
                        </Box>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    disabled={!newPanel.title.trim()}
                >
                    {isEditing ? 'Save Changes' : 'Create Panel'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default PanelModal; 